import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { surveyApi } from '@/services/adminApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, ClipboardList, Play, Archive } from 'lucide-react';
import { LoadingState, EmptyState } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Survey } from '@/types/admin';

export const SurveyListPage: React.FC = () => {
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'survey');
  const canEdit = hasPermission('edit', 'survey');
  const canDelete = hasPermission('delete', 'survey');
  const canPublish = hasPermission('publish', 'survey');
  const canArchive = hasPermission('archive', 'survey');

  // Build fetch function with filters
  const fetchSurveysFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      const listParams: { page: number; size: number; type?: string; status?: string } = {
        page: params.page,
        size: params.size,
      };
      if (filterType !== 'all') listParams.type = filterType;
      if (filterStatus !== 'all') listParams.status = filterStatus;
      return surveyApi.list(listParams);
    };
  }, [filterType, filterStatus]);

  // Use paginated list hook
  const {
    data: surveys,
    isLoading,
    error,
    pagination,
    setPage,
    setSize: _setSize,
    refetch,
  } = usePaginatedList<Survey>({
    fetchFn: fetchSurveysFn,
    entityName: 'surveys',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await surveyApi.delete(id);
    },
    entityName: 'survey',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      await surveyApi.publish(id);
    },
    entityName: 'survey',
    operation: 'update',
    successMessage: 'Survey published successfully',
    onSuccess: () => {
      refetch();
    },
  });

  // Archive mutation
  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      await surveyApi.archive(id);
    },
    entityName: 'survey',
    operation: 'update',
    successMessage: 'Survey archived successfully',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;
    await deleteMutation.mutate(id);
  };

  const handlePublish = async (id: string) => {
    await publishMutation.mutate(id);
  };

  const handleArchive = async (id: string) => {
    await archiveMutation.mutate(id);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Survey Management</h1>
          <p className="text-muted-foreground mt-2">Manage all surveys</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/surveys/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Survey
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="FEEDBACK">Feedback</SelectItem>
                <SelectItem value="ASSESSMENT">Assessment</SelectItem>
                <SelectItem value="QUIZ">Quiz</SelectItem>
                <SelectItem value="POLL">Poll</SelectItem>
                <SelectItem value="RESEARCH">Research</SelectItem>
                <SelectItem value="ONBOARDING">Onboarding</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="PUBLISHED">Published</SelectItem>
                <SelectItem value="PAUSED">Paused</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
                <SelectItem value="DELETED">Deleted</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={refetch}>Apply Filters</Button>
          </div>
        </CardContent>
      </Card>

      {/* Survey Table */}
      <Card>
        <CardHeader>
          <CardTitle>Surveys ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all surveys</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading surveys..." />
          ) : (error || surveys.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load surveys. Please try again or create a new one." : "Get started by creating your first survey."}
              icon={ClipboardList}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/surveys/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Survey ID</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Questions</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {surveys.map((survey) => (
                    <TableRow key={survey.id}>
                      <TableCell className="font-mono text-sm">{survey.surveyId}</TableCell>
                      <TableCell className="font-medium">{survey.title}</TableCell>
                      <TableCell>{survey.type}</TableCell>
                      <TableCell>{survey.category}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            survey.status === 'PUBLISHED'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : survey.status === 'DRAFT'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {survey.status}
                        </span>
                      </TableCell>
                      <TableCell>{survey.questions?.length || 0}</TableCell>
                      <TableCell>
                        {formatDate(survey.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/surveys/${survey.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/surveys/${survey.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canPublish && survey.status === 'DRAFT' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => survey.id && handlePublish(survey.id)}
                              title="Publish"
                              disabled={publishMutation.isLoading}
                            >
                              <Play className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {canArchive && survey.status !== 'ARCHIVED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => survey.id && handleArchive(survey.id)}
                              title="Archive"
                              disabled={archiveMutation.isLoading}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => survey.id && handleDelete(survey.id)}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {(pagination.totalPages > 1 || pagination.totalElements > pagination.size) && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {pagination.page + 1} of {pagination.totalPages || 1} ({pagination.totalElements} total)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.max(0, pagination.page - 1))}
                      disabled={pagination.page === 0 || isLoading}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(Math.min((pagination.totalPages || 1) - 1, pagination.page + 1))}
                      disabled={pagination.page >= (pagination.totalPages || 1) - 1 || isLoading}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SurveyListPage;
